import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useWorkSession } from '../context/WorkSessionContext';
import { subscribeToEdits, subscribeToSessions } from '../firebase/firestoreService';
import './Dashboard.css';

// ── Helpers ────────────────────────────────────────────────────────────────

function fmtDuration(sec) {
  if (!sec || sec < 0) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function fmtTimestamp(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// eslint-disable-next-line no-unused-vars
function fmtDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function dayKey(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toISOString().slice(0, 10);
}

function valueDisplay(v) {
  if (v === null || v === undefined) return <em style={{ color: '#475569' }}>empty</em>;
  if (Array.isArray(v)) return v.join(' | ');
  if (typeof v === 'object') return JSON.stringify(v).slice(0, 120);
  const s = String(v);
  return s.length > 120 ? s.slice(0, 120) + '…' : s;
}

// ── Component ──────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { isAdmin } = useWorkSession();

  const [edits, setEdits]       = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [tab, setTab]           = useState('overview'); // overview | edits | sessions | chemists
  const [expandEdit, setExpandEdit] = useState(null);

  // Real-time subscriptions
  useEffect(() => {
    const unsub1 = subscribeToEdits(data => { setEdits(data); setLoading(false); });
    const unsub2 = subscribeToSessions(data => setSessions(data));
    return () => { unsub1(); unsub2(); };
  }, []);

  // ── Computed stats ───────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const totalEdits    = edits.length;
    const totalSessions = sessions.length;
    const activeSessions = sessions.filter(s => s.active).length;

    // Total logged time across all sessions
    const totalTimeSec = sessions.reduce((sum, s) => sum + (s.durationSec || 0), 0);

    // Unique compounds touched
    const compoundSet = new Set(edits.map(e => e.compoundId));
    const compoundsTouched = compoundSet.size;

    // Fields changed total
    const fieldsChanged = edits.reduce((sum, e) => sum + (e.fieldCount || 0), 0);

    // Edits by day (last 14 days)
    const byDay = {};
    edits.forEach(e => {
      const k = dayKey(e.timestamp);
      if (k) byDay[k] = (byDay[k] || 0) + 1;
    });

    // Edits by compound
    const byCompound = {};
    edits.forEach(e => {
      byCompound[e.compoundId] = (byCompound[e.compoundId] || { name: e.compoundName, count: 0 });
      byCompound[e.compoundId].count++;
    });

    // Edits by chemist
    const byUser = {};
    edits.forEach(e => {
      if (!byUser[e.uid]) byUser[e.uid] = { name: e.displayName, editCount: 0, fields: 0 };
      byUser[e.uid].editCount++;
      byUser[e.uid].fields += e.fieldCount || 0;
    });
    // Add session time to user stats
    sessions.forEach(s => {
      if (s.uid && byUser[s.uid]) {
        byUser[s.uid].timeSec = (byUser[s.uid].timeSec || 0) + (s.durationSec || 0);
      }
    });

    return { totalEdits, totalSessions, activeSessions, totalTimeSec, compoundsTouched, fieldsChanged, byDay, byCompound, byUser };
  }, [edits, sessions]);

  // Last 14 days for the bar chart
  const last14 = useMemo(() => {
    const days = [];
    for (let i = 13; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const k = d.toISOString().slice(0, 10);
      days.push({ key: k, label: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }), count: stats.byDay[k] || 0 });
    }
    return days;
  }, [stats.byDay]);

  const maxDayCount = Math.max(...last14.map(d => d.count), 1);

  if (!isAdmin) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: '#64748b' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <h2>Admin Access Required</h2>
        <p>This dashboard is only visible to admin users.</p>
        <Link to="/" style={{ color: '#6366f1' }}>← Go Home</Link>
      </div>
    );
  }

  return (
    <div className="dash-page">
      {/* Hero */}
      <div className="dash-hero">
        <div className="container">
          <div className="dash-hero-top">
            <div>
              <h1 className="dash-title">📊 Research Activity Dashboard</h1>
              <p className="dash-subtitle">Real-time tracking of chemist edits, sessions, and time spent</p>
            </div>
            {stats.activeSessions > 0 && (
              <div className="dash-live-badge">
                <span className="dash-live-dot" />
                {stats.activeSessions} active session{stats.activeSessions !== 1 ? 's' : ''}
              </div>
            )}
          </div>

          {/* KPI row */}
          <div className="dash-kpi-row">
            {[
              { icon: '✏️', label: 'Total Edits',       value: stats.totalEdits },
              { icon: '🧪', label: 'Fields Updated',    value: stats.fieldsChanged },
              { icon: '🔬', label: 'Compounds Touched', value: stats.compoundsTouched },
              { icon: '⏱',  label: 'Total Time Logged', value: fmtDuration(stats.totalTimeSec) },
              { icon: '💼', label: 'Work Sessions',     value: stats.totalSessions },
            ].map(k => (
              <div className="dash-kpi" key={k.label}>
                <span className="dash-kpi-icon">{k.icon}</span>
                <span className="dash-kpi-value">{loading ? '…' : k.value}</span>
                <span className="dash-kpi-label">{k.label}</span>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="dash-tabs">
            {['overview', 'edits', 'sessions', 'chemists'].map(t => (
              <button key={t} className={`dash-tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
                {t === 'overview'  && '📈 Overview'}
                {t === 'edits'     && `✏️ Edit Log (${stats.totalEdits})`}
                {t === 'sessions'  && `💼 Sessions (${stats.totalSessions})`}
                {t === 'chemists'  && '👥 By Chemist'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="dash-body">
        <div className="container">

          {/* ── OVERVIEW ── */}
          {tab === 'overview' && (
            <div className="dash-overview">
              {/* Activity bar chart */}
              <div className="dash-card">
                <h2 className="dash-card-title">Edits per Day — Last 14 Days</h2>
                <div className="dash-bar-chart">
                  {last14.map(d => (
                    <div key={d.key} className="dash-bar-col">
                      <div className="dash-bar-wrap">
                        <div
                          className="dash-bar"
                          style={{ height: `${Math.max(4, (d.count / maxDayCount) * 100)}%` }}
                          title={`${d.count} edit${d.count !== 1 ? 's' : ''}`}
                        />
                      </div>
                      <span className="dash-bar-label">{d.label}</span>
                      {d.count > 0 && <span className="dash-bar-count">{d.count}</span>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Top compounds + recent edits side by side */}
              <div className="dash-two-col">
                <div className="dash-card">
                  <h2 className="dash-card-title">Most Edited Compounds</h2>
                  {Object.entries(stats.byCompound)
                    .sort((a, b) => b[1].count - a[1].count)
                    .slice(0, 8)
                    .map(([id, info]) => (
                      <div key={id} className="dash-rank-row">
                        <Link to={`/compounds/${id}`} className="dash-rank-name">
                          {info.name || id}
                        </Link>
                        <div className="dash-rank-bar-wrap">
                          <div
                            className="dash-rank-bar"
                            style={{ width: `${(info.count / (Object.values(stats.byCompound)[0]?.count || 1)) * 100}%` }}
                          />
                        </div>
                        <span className="dash-rank-count">{info.count}</span>
                      </div>
                    ))}
                  {Object.keys(stats.byCompound).length === 0 && (
                    <p className="dash-empty">No edits recorded yet.</p>
                  )}
                </div>

                <div className="dash-card">
                  <h2 className="dash-card-title">Recent Activity</h2>
                  <div className="dash-feed">
                    {edits.slice(0, 12).map(e => (
                      <div key={e.id} className="dash-feed-item">
                        <div className="dash-feed-dot" />
                        <div className="dash-feed-content">
                          <div className="dash-feed-main">
                            <span className="dash-feed-user">{e.displayName}</span>
                            {' edited '}
                            <Link to={`/compounds/${e.compoundId}`} className="dash-feed-compound">
                              {e.compoundName || e.compoundId}
                            </Link>
                          </div>
                          <div className="dash-feed-meta">
                            {e.fieldCount} field{e.fieldCount !== 1 ? 's' : ''} · {fmtTimestamp(e.timestamp)}
                          </div>
                        </div>
                      </div>
                    ))}
                    {edits.length === 0 && <p className="dash-empty">No edits yet.</p>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── EDIT LOG ── */}
          {tab === 'edits' && (
            <div className="dash-card">
              <h2 className="dash-card-title">Complete Edit Log</h2>
              <p className="dash-card-desc">Every field change, who made it, and when. Click a row to see the before/after diff.</p>
              <div className="dash-table-wrap">
                <table className="dash-table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Chemist</th>
                      <th>Compound</th>
                      <th>Fields</th>
                      <th>Time on Page</th>
                      <th>Diff</th>
                    </tr>
                  </thead>
                  <tbody>
                    {edits.map(e => (
                      <React.Fragment key={e.id}>
                        <tr
                          className={`dash-tr ${expandEdit === e.id ? 'expanded' : ''}`}
                          onClick={() => setExpandEdit(expandEdit === e.id ? null : e.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td className="dash-td-ts">{fmtTimestamp(e.timestamp)}</td>
                          <td className="dash-td-user">{e.displayName}</td>
                          <td>
                            <Link
                              to={`/compounds/${e.compoundId}`}
                              className="dash-compound-link"
                              onClick={ev => ev.stopPropagation()}
                            >
                              {e.compoundName || e.compoundId}
                            </Link>
                          </td>
                          <td>
                            <span className="dash-fields-badge">{e.fieldCount} field{e.fieldCount !== 1 ? 's' : ''}</span>
                          </td>
                          <td className="dash-td-dur">{fmtDuration(e.durationSec)}</td>
                          <td className="dash-td-expand">{expandEdit === e.id ? '▲' : '▼'}</td>
                        </tr>
                        {expandEdit === e.id && (
                          <tr className="dash-diff-row">
                            <td colSpan={6}>
                              <div className="dash-diff">
                                {Object.entries(e.fields || {}).map(([field, change]) => (
                                  <div key={field} className="dash-diff-field">
                                    <div className="dash-diff-field-name">{field}</div>
                                    <div className="dash-diff-before">
                                      <span className="dash-diff-label">Before</span>
                                      <span className="dash-diff-value">{valueDisplay(change.before)}</span>
                                    </div>
                                    <div className="dash-diff-after">
                                      <span className="dash-diff-label">After</span>
                                      <span className="dash-diff-value">{valueDisplay(change.after)}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
                {edits.length === 0 && <div className="dash-empty-table">No edits recorded yet.</div>}
              </div>
            </div>
          )}

          {/* ── SESSIONS ── */}
          {tab === 'sessions' && (
            <div className="dash-card">
              <h2 className="dash-card-title">Work Sessions</h2>
              <p className="dash-card-desc">Every login session with duration, edits made, and compounds touched.</p>
              <div className="dash-table-wrap">
                <table className="dash-table">
                  <thead>
                    <tr>
                      <th>Chemist</th>
                      <th>Started</th>
                      <th>Ended</th>
                      <th>Duration</th>
                      <th>Edits</th>
                      <th>Compounds</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map(s => (
                      <tr key={s.id} className={s.active ? 'dash-tr-active' : ''}>
                        <td className="dash-td-user">{s.displayName}</td>
                        <td className="dash-td-ts">{fmtTimestamp(s.startedAt)}</td>
                        <td className="dash-td-ts">{s.active ? '—' : fmtTimestamp(s.endedAt)}</td>
                        <td className="dash-td-dur">
                          {s.active
                            ? <span className="dash-live-text">● Live</span>
                            : fmtDuration(s.durationSec)
                          }
                        </td>
                        <td>{s.editCount || 0}</td>
                        <td>
                          <span title={(s.compoundsTouched || []).join(', ')}>
                            {(s.compoundsTouched || []).length}
                          </span>
                        </td>
                        <td>
                          <span className={`dash-session-badge ${s.active ? 'active' : 'done'}`}>
                            {s.active ? 'Active' : 'Complete'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {sessions.length === 0 && <div className="dash-empty-table">No sessions recorded yet.</div>}
              </div>
            </div>
          )}

          {/* ── CHEMISTS ── */}
          {tab === 'chemists' && (
            <div className="dash-chemists">
              {Object.entries(stats.byUser).length === 0 && (
                <div className="dash-card">
                  <p className="dash-empty">No chemist activity recorded yet.</p>
                  <p style={{ color: '#64748b', fontSize: '0.85rem', marginTop: 8 }}>
                    Chemists will appear here once they log in and make their first edit.
                  </p>
                </div>
              )}
              {Object.entries(stats.byUser).map(([uid, u]) => (
                <div key={uid} className="dash-card dash-chemist-card">
                  <div className="dash-chemist-header">
                    <div className="dash-chemist-avatar">{(u.name || '?')[0].toUpperCase()}</div>
                    <div>
                      <div className="dash-chemist-name">{u.name}</div>
                      <div className="dash-chemist-uid">{uid}</div>
                    </div>
                  </div>
                  <div className="dash-chemist-stats">
                    <div className="dash-cstat">
                      <span className="dash-cstat-val">{u.editCount}</span>
                      <span className="dash-cstat-lbl">Saves</span>
                    </div>
                    <div className="dash-cstat">
                      <span className="dash-cstat-val">{u.fields}</span>
                      <span className="dash-cstat-lbl">Fields Updated</span>
                    </div>
                    <div className="dash-cstat">
                      <span className="dash-cstat-val">{fmtDuration(u.timeSec)}</span>
                      <span className="dash-cstat-lbl">Time Logged</span>
                    </div>
                  </div>
                  <div className="dash-chemist-edits">
                    <h4>Recent Edits</h4>
                    {edits.filter(e => e.uid === uid).slice(0, 5).map(e => (
                      <div key={e.id} className="dash-chemist-edit-row">
                        <Link to={`/compounds/${e.compoundId}`} className="dash-feed-compound">
                          {e.compoundName || e.compoundId}
                        </Link>
                        <span style={{ color: '#64748b', fontSize: '0.8rem' }}>
                          {e.fieldCount} field{e.fieldCount !== 1 ? 's' : ''} · {fmtTimestamp(e.timestamp)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
